/**
 * ResultScreen.tsx — the ELIGIBLE result ("treatment could be suitable").
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign);
 * premium pass 2026-09-24 (Build B — loader + Result).
 *
 * WHY: this screen renders ONLY for a positive submit verdict —
 * eligibility 'eligible' or 'clinician_review'. The `not_eligible` branch never
 * reaches here (JourneyPage routes it to the StopScreen — CLIN-02). It shows the
 * projection again (with its mandatory disclaimer), the BMI dial with the
 * patient's marker, and the single terracotta CTA into plan selection. A
 * clinician-review verdict adds a calm "a clinician will look at a few answers"
 * note. Copy on this screen's own strings is category-level (R28); POM names and
 * prices only appear on the following plan-selection surface.
 *
 * TALKING TO THE PATIENT (premium pass, 2026-09-24 — CTO: "You can do way
 * better on this screen … don't remove any of these things … talk to the
 * patient … they are on their journey … it's just a few steps more"). Every
 * element the screen had is still here — the verdict heading and its line, the
 * projection with its disclaimer beside the figure, the kg/st toggle, the BMI
 * dial and its caption, "What happens next", "See my plan", the fee line, the
 * call-back offer. It ADDS, from `resultCopy.ts`: a greeting (a first name only
 * when the journey already holds one in memory — never fetched, never logged),
 * an encouraging line that promises nothing about weight (CAP 13), a "Your
 * journey" tracker (health check done · choose your plan next · delivery & card
 * · clinician review), and a trust row made ONLY of claims the portal already
 * makes (the kit's `TrustStrip` chips + this screen's own "Discreet delivery").
 * No testimonial or before/after carousel: that needs consented real patients
 * and is the CTO's call, not this screen's.
 *
 * LAYOUT. At >=880px the WIDE column (64rem) holds a HERO of two columns — LEFT
 * the greeting, verdict, tracker and the "See my plan" CTA; RIGHT the projection
 * card, the hero visual, on the soft bloom — and under it a second row with the
 * BMI dial and "What happens next" side by side (`.gps-result-columns`). The
 * hero, the projection and the CTA are above the fold at 1440x900. Below 880px
 * everything stacks in DOM order (greeting → verdict → tracker → trust →
 * projection → BMI → what happens next → call-back offer). Without measurements
 * there is no projection or dial to show, so the screen keeps the classic narrow
 * column (a half-empty two-column grid would be the dead-space complaint
 * rebuilt).
 * ⚠️ WIDTH ONLY, NEVER A TRANSFORM on anything that wraps the CTA: below 880px
 * it is `position: fixed`, and a transformed/filtered ancestor re-anchors it —
 * which is why `.gps-plan-breakout` (translateX) must never wrap this screen.
 *
 * ONE "SEE MY PLAN" BUTTON, TWO PLACES. There is exactly one button element (one
 * handler — the same `onSeePlan` the funnel tracking hangs off). At >=880px it
 * sits INLINE in the hero, above the fold. Below 880px its wrapper
 * (`.gps-jux-result-cta`, tokens.css P9) becomes a viewport-fixed, OPAQUE bottom
 * bar, so the conversion action is on screen from the first paint on a phone.
 * The page then reserves MORE than the bar's height at the very END of the
 * document — padding on the app shell, after the site's legal footer, keyed by
 * `:root:has(… .gps-jux-result-cta)` — so the patient can scroll everything
 * clear of it: the BMI caption, the call-back offer AND the footer's
 * regulatory line (walkthrough polish item 6: the old bar covered the dial at
 * 1440x900 and let its caption ghost through; the first premium cut reserved
 * space inside the column, which still left the footer under the bar on a
 * phone). Both rules are CSS because an inline style cannot carry a media
 * query; jsdom applies neither, so `ResultScreen.spec` pins the rules
 * themselves and the geometry is a browser check.
 *
 * KEEPING IN TOUCH MOVED OFF THIS SCREEN (2026-09-23, CTO — the card cost two
 * extra clicks). The marketing-consent boxes now sit on the questionnaire's
 * consent screen (QuestionCard's ConsentQuestion, `marketing` prop) and the
 * grant is recorded by JourneyPage's runSubmit the moment the submit succeeds
 * — see marketing/journeyConsent.ts for the post-submit server guard that
 * shapes that. This screen holds NO consent state, makes NO marketing read or
 * write, and "See my plan" is synchronous again. `/preferences` remains the
 * change-your-mind surface. Do not re-add a consent card here.
 */
import { useId, type ReactNode } from 'react';
import { CheckIcon, Help, TrustStrip } from '../ui/kit';
import { BmiDial } from './BmiDial';
import { JourneyButton } from './JourneyUi';
import { JourneyScreen } from './JourneyScreen';
import { ProjectionChart } from './ProjectionChart';
import { bmiOf } from './projection';
import {
  DISCREET_DELIVERY,
  JOURNEY_TRACKER_STATE_WORDS,
  RESULT_ENCOURAGEMENT,
  RESULT_JOURNEY_HEADING,
  RESULT_JOURNEY_STEPS,
  RESULT_STEPS_LEFT,
  bmiCaption,
  greetingName,
  resultGreeting,
} from './resultCopy';
import type { AssessmentSubmitResult } from '../api/types';

/** Eligible result: greeting, verdict, tracker and "See my plan" beside the
 * projection, with the BMI dial and what happens next below. */
export function ResultScreen({
  heightCm,
  weightKg,
  result,
  onSeePlan,
  callbackOffer,
  firstName,
}: {
  heightCm: number;
  weightKg: number;
  result: AssessmentSubmitResult;
  onSeePlan: () => void;
  /**
   * The in-journey call-back offer (JourneyPage). Rendered INSIDE the column,
   * after "What happens next" — the JourneyScreen `footer` slot.
   */
  callbackOffer?: ReactNode;
  /**
   * The patient's first name, ONLY as typed on Step 0 of this journey and held
   * in memory by JourneyPage. Absent → no name. Never fetched or logged for
   * this — see `resultCopy.ts`.
   */
  firstName?: string | null;
}) {
  /**
   * THE MEASUREMENTS ARE OPTIONAL AT RENDER TIME, THE VERDICT IS NOT.
   *
   * JourneyPage used to gate this whole screen on `heightCm > 0 && weightKg > 0`
   * with nothing on the other side, so a patient who reached the result without
   * them — a cold `?assessment=` restore on a question set with no leading
   * `capture` question, or an eligible submit whose best-effort lead re-source
   * could not recover them — got a submitted assessment and a page with a header,
   * a footer and nothing in between. That guard was defending the right thing
   * (`bmiOf(0, 0)` is NaN and `ProjectionChart` would state a confident "around
   * 0 kg"); it just had nowhere to send the patient.
   *
   * So the guard moves in here, per BLOCK rather than per SCREEN. The verdict,
   * the "what happens next" steps and the CTA into plan selection need no
   * measurements and always render; the projection and the BMI dial are the only
   * two things that do, and they are omitted rather than faked. Showing a
   * treatment decision with one card fewer is a much smaller failure than showing
   * nothing at all — and a far smaller one than inventing numbers on a screen a
   * patient is about to make a clinical decision from.
   */
  const hasMeasurements =
    Number.isFinite(heightCm) && heightCm > 0 && Number.isFinite(weightKg) && weightKg > 0;
  const bmi = bmiOf(heightCm, weightKg);
  const needsReview = result.eligibility === 'clinician_review' || result.flags.length > 0;
  const journeyHeadingId = useId();
  const name = greetingName(firstName);

  // LEFT of the hero at >=880px, first in the stack below: the patient, the
  // verdict, where they are on the way, and the one conversion action.
  const lead = (
    <div className="gps-jux-result-lead">
      <p className="gps-jux-result-greeting">
        <span aria-hidden="true" className="gps-jux-result-check">
          <CheckIcon size={16} />
        </span>
        <span className="gps-jux-moment-eyebrow">{resultGreeting(name)}</span>
      </p>
      <h1 className="gps-jux-moment-title gps-jux-result-title">Good news — treatment could be suitable</h1>
      {/* One line only. The old screen said "a clinician will review…" in BOTH
          the subhead and a separate info box below — this folds the review +
          the "only charged after approval" reassurance into a single sentence,
          and the per-step detail lives in the "What happens next" card. */}
      <Help style={{ margin: '0 0 var(--gps-s-4)' }}>
        A clinician reviews your answers before anything is dispensed — and you&rsquo;re only charged
        once it&rsquo;s approved.
      </Help>

      <div className="gps-jux-moment-card gps-jux-result-journey">
        <div className="gps-jux-result-journey-head">
          <h2 id={journeyHeadingId} className="gps-jux-result-label">
            {RESULT_JOURNEY_HEADING}
          </h2>
          <p className="gps-jux-result-encourage">
            {RESULT_ENCOURAGEMENT} <span className="gps-jux-result-left">{RESULT_STEPS_LEFT}</span>
          </p>
        </div>
        {/* NO `aria-current` on any step (review, 2026-09-25). The patient is ON
            the Result screen, not on "Choose your plan", and a screen reader
            would hear that step as "next" AND "current step" at once. The
            hidden state words carry the state (done / next / still to come);
            the dot and the bold label carry it visually. */}
        <ol className="gps-jux-result-track" aria-labelledby={journeyHeadingId}>
          {RESULT_JOURNEY_STEPS.map((step) => (
            <li
              key={step.label}
              className="gps-jux-result-track-step"
              data-track-state={step.state}
            >
              <span aria-hidden="true" className="gps-jux-result-track-mark">
                {step.state === 'done' ? <CheckIcon size={12} /> : null}
              </span>
              <span className="gps-jux-result-track-label">{step.label}</span>
              <span className="gps-sr-only">{JOURNEY_TRACKER_STATE_WORDS[step.state]}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* THE ONE "See my plan" button — inline here at >=880px, a fixed opaque
          bar below 880px (the wrapper's class does it; see the header). */}
      <div className="gps-jux-result-cta">
        <div className="gps-jux-result-cta-inner">
          <JourneyButton
            type="button"
            variant="cta"
            onClick={onSeePlan}
            arrow
            style={{ minHeight: '54px', fontSize: '15.5px', fontWeight: 600 }}
          >
            See my plan
          </JourneyButton>
          <p className="gps-jux-result-fee">
            Medication only · no consultation fee ·{' '}
            <b style={{ color: 'var(--gps-green-text)' }}>charged only after approval</b>
          </p>
        </div>
      </div>

      {/* Claims the portal already makes, and nothing new: the kit's two
          standing chips, and this screen's own "Discreet delivery" step in the
          third slot (the kit's default third chip would repeat the fee line). */}
      <div className="gps-jux-result-trust">
        <TrustStrip paymentCue={DISCREET_DELIVERY} />
      </div>
    </div>
  );

  const nextSteps = (
    <section className="gps-jux-moment-card">
      <SectionLabel>What happens next</SectionLabel>
      <NextStep n={1} title="Clinician review">
        {needsReview
          ? 'A clinician checks your answers and may follow up on a couple with you.'
          : 'A clinician checks your answers to confirm this treatment is right for you.'}
      </NextStep>
      <NextStep n={2} title="Approval & payment">
        Your card is only charged once your treatment is approved — never before.
      </NextStep>
      <NextStep n={3} title={DISCREET_DELIVERY} last>
        Sent to your door in plain, insulated packaging.
      </NextStep>
    </section>
  );

  return (
    // Wide ONLY when there is a right column to fill — a two-column grid with
    // an empty half is the dead space this layout exists to remove.
    <JourneyScreen wide={hasMeasurements} percent={100} step="Done" footer={callbackOffer}>
      <div className="gps-jux-result">
        <div className="gps-jux-result-hero gps-jux-bloom" data-columns={hasMeasurements ? '2' : '1'}>
          {lead}
          {hasMeasurements ? (
            // RIGHT of the hero: the projection. The figure and its mandatory
            // disclaimer stay together inside ProjectionChart.
            <section className="gps-jux-moment-card gps-jux-result-visual">
              <SectionLabel>Your projected progress</SectionLabel>
              <ProjectionChart weightKg={weightKg} />
            </section>
          ) : null}
        </div>

        {hasMeasurements ? (
          <div className="gps-result-columns gps-jux-result-more">
            <section className="gps-jux-moment-card">
              <SectionLabel>Where your BMI sits</SectionLabel>
              <BmiDial bmi={bmi} />
              <p style={{ fontSize: '13px', color: 'var(--gps-muted)', margin: 0, textAlign: 'center' }}>
                {bmiCaption(bmi)}
              </p>
            </section>
            {nextSteps}
          </div>
        ) : (
          <div className="gps-jux-result-more">{nextSteps}</div>
        )}
      </div>
    </JourneyScreen>
  );
}

/** A small uppercase card heading — the shared hierarchy cue for each card on
 * this screen (journey, projection, BMI, what happens next). A real <h2> (under
 * the screen's <h1>) so screen-reader users can navigate the Result screen by
 * heading (WCAG — #79 review); `.gps-jux-result-label` makes it read as a label. */
function SectionLabel({ children }: { children: ReactNode }) {
  return <h2 className="gps-jux-result-label">{children}</h2>;
}

/** One numbered row in the "What happens next" card: a step chip, a bold title
 * and a plain-language line. `last` drops the divider under the final row. */
function NextStep({
  n,
  title,
  children,
  last = false,
}: {
  n: number;
  title: string;
  children: ReactNode;
  last?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--gps-s-3)',
        alignItems: 'flex-start',
        padding: '10px 0',
        borderBottom: last ? 'none' : '1px solid var(--gps-border)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flex: '0 0 auto',
          width: '24px',
          height: '24px',
          borderRadius: 'var(--gps-r-pill)',
          background: 'var(--gps-jux-loader-disc, #e6ede6)',
          color: 'var(--gps-jux-loader-ink, #0a4a2e)',
          display: 'grid',
          placeItems: 'center',
          fontSize: '13px',
          fontWeight: 700,
        }}
      >
        {n}
      </span>
      <span style={{ display: 'block' }}>
        <span style={{ display: 'block', fontSize: '14.5px', fontWeight: 600, color: 'var(--gps-text)' }}>{title}</span>
        <span style={{ display: 'block', fontSize: '13.5px', color: 'var(--gps-muted)', marginTop: '1px' }}>
          {children}
        </span>
      </span>
    </div>
  );
}
